import { useMediaQuery } from '@react-hook/media-query'
import { IconLogout } from '@tabler/icons-react'

import { useLocalStorage, useNavigation } from '@subaio/components'
import { Form } from '@subaio/components/src/HookForm'
import type { KeycloakUser } from '@subaio/utils'

import { ButtonIcon } from 'components/Buttons'
import { CodeSelect } from 'components/FormFields'
import { Logo } from 'components/Logo'
import { useModal } from 'components/Modal'
import { Location, Tooltip } from 'components/Tooltip'

import { SearchMenu } from '../SearchMenu'
import { Wrapper } from '../SearchMenu/Wrapper'
import { AlignmentWrapper } from './AlignmentWrapper'
import { ButtonSearch } from './ButtonSearch'
import { Container } from './Container'
import { Content } from './Content'
import { DeveloperButton } from './DeveloperButton'
import { LogoLink } from './LogoLink'
import { Name } from './Name'

type Props = {
	developer?: KeycloakUser
	onLogout: () => void
	isLoginExpired: boolean
}

export const Header = ({ developer, isLoginExpired, onLogout }: Props) => {
	const isDesktop = useMediaQuery('only screen and (min-width: 768px)')
	const [overrideColorScheme, setOverrideColorScheme] = useLocalStorage<'dark' | 'light' | 'system'>(
		'colorScheme',
		'system',
	)
	const { onNavigateTo } = useNavigation()
	const { showRaw } = useModal()

	return (
		<Container>
			<Content>
				<AlignmentWrapper align='left'>
					<LogoLink name='Docs'>
						<Logo size={100} type='header' />
						<Name>
							<span>Developer</span>
							<span>Docs</span>
						</Name>
					</LogoLink>
				</AlignmentWrapper>
				<AlignmentWrapper align={isDesktop ? 'center' : 'right'}>
					<ButtonSearch
						isDesktop={isDesktop}
						onClick={() =>
							showRaw({
								content: (key, { onClose, duration, status }) => (
									<Wrapper key={key} duration={duration} status={status}>
										<SearchMenu onClose={onClose} />
									</Wrapper>
								),
								duration: 0.2,
							})
						}
					/>
				</AlignmentWrapper>
				{isDesktop && (
					<AlignmentWrapper align='right'>
						<Form<{ overrideColorScheme: 'light' | 'dark' | 'system' }>
							initialValues={{ overrideColorScheme }}
							render={() => (
								<CodeSelect
									name='overrideColorScheme'
									label=''
									options={[
										{ label: 'System', value: 'system' },
										{ label: 'Light', value: 'light' },
										{ label: 'Dark', value: 'dark' },
									]}
									isClearable={false}
								/>
							)}
							onSubmit={() => undefined}
							onChange={({ overrideColorScheme }) => setOverrideColorScheme(overrideColorScheme!)}
						/>
						{isLoginExpired && <div>Your login has expired</div>}
						{developer && (
							<>
								<Tooltip tip='To developer home' location={Location.Left}>
									<DeveloperButton name='DeveloperHome'>
										<small>Developer</small>
										<div>{developer.displayName}</div>
									</DeveloperButton>
								</Tooltip>
								<Tooltip tip='Logout' location={Location.Left}>
									<ButtonIcon
										icon={<IconLogout strokeWidth={1.5} />}
										type='textHeader'
										removeAlignment
										onClick={() => {
											onNavigateTo({ name: 'Home' })
											onLogout()
										}}
									/>
								</Tooltip>
							</>
						)}
					</AlignmentWrapper>
				)}
			</Content>
		</Container>
	)
}
